import {gsap} from "gsap/all";
import $ from "jquery";

// A gradient fill is revealed by clipping it at a marker that rides a hidden
// centerline path, so the marker always sits at the leading edge of the fill.
// Works for both straight and curved tracks. Drive it with setLevel, from 0 to 1.
//
// The centerline decides which way the gauge fills: it is drawn from the empty
// end to the full end, so a path going upwards fills from the bottom (the fuel
// gauge) and one going downwards fills from the top (the ground distance gauge).
//
// Markup it needs (pass the gauge element to new Gauge(el)):
//   an svg with:
//     .gauge-centerline  hidden path (empty end to full end) the marker follows
//     .gauge-clip        rect inside a clipPath; its y and height show the fill
//   .gauge-marker        HTML element next to the svg, moved to the leading edge
//                        of the fill via left/top, like a glass disc
// Optional:
//   .gauge-value         element whose text shows the current value; updated each
//                        frame from the level and the data-min/max range
//   .gauge-gradient      linearGradient of the fill; its far end is pulled to the
//                        leading edge so the whole ramp of colors is squeezed into
//                        whatever is filled, instead of being revealed one color
//                        at a time. Leave the class off to keep the ramp fixed.
//   .gauge-clip-cap      circle sharing the clipPath with the rect, which rounds
//                        off the leading edge instead of cutting it square. The
//                        rect stops at its center so the two join flush.
// Data attributes on the gauge element:
//   data-level           starting level, 0 to 1
//   data-min / data-max  range of the .gauge-value readout
//   data-decimals        decimals on that readout (default 1). It is always
//                        thousands separated.
//   data-fill-overshoot  how far past the marker's center the fill runs, in
//                        viewBox units. Negative stops it short, for a marker
//                        the fill should meet at its edge rather than run under.
//   data-cap-radius      radius of the .gauge-clip-cap, as "empty,full" when the
//                        track tapers, or a single number when it does not. Match
//                        it to the track's half width so the cap reads as a round
//                        end rather than a blunt one.

export class Gauge {
  constructor(el) {
    this.$el = $(el);
    this.el = this.$el.get(0);
    if (!this.el) return; // nothing in the DOM — setLevel() becomes a no-op

    this.$marker = this.$el.find(".gauge-marker");
    this.$clipRect = this.$el.find(".gauge-clip");
    this.$valueEl = this.$el.find(".gauge-value");
    // Raw nodes kept for SVG geometry (getTotalLength / getPointAtLength / viewBox).
    this.svg = this.$el.find("svg").get(0);
    this.centerline = this.$el.find(".gauge-centerline").get(0);

    // Readout range: value = min + level * (max - min).
    this.min = parseFloat(this.$el.data("min")) || 0;
    this.max = this.$el.data("max") != null ? parseFloat(this.$el.data("max")) : 1;
    // How many decimals it prints: none for a gauge counting in thousands, one for a
    // gauge reading out a couple of units.
    this.decimals = this.$el.data("decimals") != null ? parseInt(this.$el.data("decimals"), 10) : 1;
    // The clip runs from the marker's Y to the edge of the viewBox on the full
    // side of the track, so the fill always covers the part already travelled.
    this.viewBoxHeight = this.svg ? this.svg.viewBox.baseVal.height : 0;
    // A centerline whose end sits lower than its start fills downwards.
    this.fillsDown = this.centerline ? this.getFillsDown() : false;
    // How far (in viewBox units) the fill runs past the marker's center. A
    // positive value tucks the leading edge under the marker so the two never
    // leave a gap, a negative one stops the fill at the marker's edge.
    this.fillOvershoot = this.$el.data("fill-overshoot") != null ? parseFloat(this.$el.data("fill-overshoot")) : 20;

    // An opted in gradient stretches with the fill. Its y1 stays anchored at the
    // empty end as authored and y2 follows the leading edge.
    this.$gradient = this.$el.find(".gauge-gradient");
    this.gradientStart = this.$gradient.length ? parseFloat(this.$gradient.attr("y1")) : 0;

    // The circle rounding off the leading edge. Its radius grows with the level
    // so it keeps matching a track that widens as it goes.
    this.$clipCap = this.$el.find(".gauge-clip-cap");
    const radii = (this.$el.data("cap-radius") + "").split(",").map(parseFloat);
    this.capRadius = {
      empty: radii[0] || 0,
      full: radii.length > 1 ? radii[1] : (radii[0] || 0)
    };

    // The peakThenSettle timeline, tracked so it can be killed on a reset.
    this.timeline = null;

    this.level = 0;
    this.setLevel(parseFloat(this.$el.data("level")) || 0, false);
  }

  // Reads the centerline's direction: true when the full end sits below the
  // empty end, meaning the gauge fills from the top down.
  getFillsDown() {
    const length = this.centerline.getTotalLength();
    if (!length) return false;
    return this.centerline.getPointAtLength(length).y > this.centerline.getPointAtLength(0).y;
  }

  // Stop any running level animation (single tween or peakThenSettle timeline).
  kill() {
    gsap.killTweensOf(this);
    if (this.timeline) {
      this.timeline.kill();
      this.timeline = null;
    }
  }

  // value goes 0 to 1 (clamped). Animates by default, since the marker spot is
  // worked out each frame and can't just be a CSS transition. Pass a longer
  // duration to stretch it out (like over the launch). The ease defaults to a
  // soft settle, but pass "none" for a steady, even change.
  setLevel(value, animate = true, duration = 0.6, ease = "power2.out") {
    if (!this.el) return this; // not in the DOM
    const target = Math.max(0, Math.min(1, value));

    this.kill();

    if (animate) {
      gsap.to(this, {
        level: target,
        duration,
        ease,
        onUpdate: () => this.render()
      });
    } else {
      this.level = target;
      this.render();
    }
    return this;
  }

  // Run a list of legs as one timeline, each easing the level to its own value
  // over its own duration. For a gauge that moves in distinct steps rather than
  // one long slide, like fuel coming down over a burn, a coast, then another burn.
  sequence(legs) {
    if (!this.el) return this; // not in the DOM
    this.kill();
    this.timeline = gsap.timeline({onUpdate: () => this.render()});
    legs.forEach(({level, duration = 0.6, ease = "none"}) => {
      this.timeline.to(this, {
        level: Math.max(0, Math.min(1, level)),
        duration: Math.max(0, duration),
        ease
      });
    });
    return this;
  }

  // Turn a readout value (like km/s) into a 0 to 1 level using the gauge's range.
  toLevel(value) {
    return (value - this.min) / (this.max - this.min);
  }

  // Rise to `peak`, hold, then ease to `end`, as three legs across the total
  // duration. `peakAt` is how far through (0 to 1) the peak is reached, `settleAt`
  // is when the descent starts. Between them the needle holds at the peak, so
  // small peakAt + large settleAt means "rush up, cruise at full, drop near the
  // end". Leave settleAt equal to peakAt for no hold. When `end` equals `peak`
  // the last leg is flat too, so the needle just holds all the way.
  peakThenSettle(peak, end, duration = 0.6, easeUp = "power1.in", easeDown = "power1.out", peakAt = 0.5, settleAt = peakAt) {
    if (!this.el) return this; // not in the DOM
    this.kill();
    const up = duration * peakAt;
    const hold = duration * Math.max(0, settleAt - peakAt);
    const down = duration - up - hold;
    this.timeline = gsap.timeline({onUpdate: () => this.render()})
      .to(this, {
        level: peak,
        duration: up,
        ease: easeUp
      })
      .to(this, {
        level: peak,
        duration: hold,
        ease: "none"
      })
      .to(this, {
        level: end,
        duration: down,
        ease: easeDown
      });
    return this;
  }

  render() {
    if (!this.centerline) return;

    const length = this.centerline.getTotalLength();
    if (!length) return;
    // The spot on the centerline at this level: the path runs from the empty
    // end at level 0 to the full end at level 1.
    const pt = this.centerline.getPointAtLength(this.level * length);

    if (this.$marker.length) {
      // Place the marker as a percent of the SVG's viewBox. The gauge element is
      // sized to the SVG, so we don't have to measure anything, and it works even
      // while the game frame is hidden.
      const vb = this.svg.viewBox.baseVal;
      this.$marker.css({
        left: `${(pt.x / vb.width) * 100}%`,
        top: `${(pt.y / vb.height) * 100}%`,
      });
    }
    // Where the fill stops, just shy of or just past the marker's center.
    const edge = this.fillsDown ? pt.y + this.fillOvershoot : pt.y - this.fillOvershoot;

    // The cap owns the last stretch of the fill, so the rect stops at its center
    // and the round end takes over from there. Without a cap the rect runs the
    // whole way and the fill ends square.
    const capR = this.$clipCap.length ? this.capRadius.empty + this.level * (this.capRadius.full - this.capRadius.empty) : 0;
    const rectEdge = this.fillsDown ? edge - capR : edge + capR;

    if (this.$clipCap.length) {
      this.$clipCap.attr({cx: pt.x, cy: rectEdge, r: capR});
    }
    if (this.$clipRect.length) {
      // Filling down the clip hangs from the top of the viewBox and grows to
      // the marker, filling up it starts at the marker and runs to the bottom.
      const top = this.fillsDown ? 0 : rectEdge;
      const bottom = this.fillsDown ? rectEdge : this.viewBoxHeight;
      this.$clipRect.attr("y", top);
      this.$clipRect.attr("height", Math.max(0, bottom - top));
    }
    if (this.$gradient.length) {
      // Squeeze the whole ramp into the filled part, so even a barely started
      // gauge shows every color rather than just the first one. Kept at least
      // a unit long, a collapsed gradient paints as a flat color.
      const end = this.fillsDown ? Math.max(this.gradientStart + 1, edge) : Math.min(this.gradientStart - 1, edge);
      this.$gradient.attr("y2", end);
    }
    if (this.$valueEl.length) {
      // Grouped, so a five figure readout reads at a glance rather than as a run of
      // digits to be counted.
      this.$valueEl.text((this.min + this.level * (this.max - this.min)).toLocaleString("en-US", {
        minimumFractionDigits: this.decimals,
        maximumFractionDigits: this.decimals
      }));
    }
  }
}
